import * as queryString from 'query-string';
import React, {useState} from 'react';
import {Redirect} from 'react-router-dom';

import {ReportDiscussionContextProvider} from '../../../components/ReportDiscussion/ReportDiscussionContext';
import {useNavigateToNewDraft} from '../../../components/ReportModals/hooks';
import {Analytics} from '../../../services/analytics';
import {useSelector} from '../../../state/hooks';
import {useViewer} from '../../../state/viewer/hooks';
import {type ReportWidthOption} from '../../../state/views/report/types';
import * as ViewSelectors from '../../../state/views/selectors';
import * as Urls from '../../../util/urls';
import {useLoadedReportContext} from '../LoadedReportContext';
import * as S from '../ReportPage.styles';
import {ReportPageContent} from '../ReportPageContent';
import {useReportPageContext} from '../ReportPageContext';
import {ReportPageEditContentHeader} from './ReportPageEditContentHeader';
import {ReportPageEditHeader} from './ReportPageEditHeader';
import ReportTipsSidebar from './ReportTipsSidebar';

type ReportPageEditContainerProps = {
  reportWidth: ReportWidthOption;
};

export const ReportPageEditContainer = ({
  reportWidth,
}: ReportPageEditContainerProps) => {
  const {
    rootReportId,
    draftId,
    viewedReportId,
    reportParent,
    entityName,
    projectName,
    reportDrafts,
  } = useReportPageContext();
  const {reportViewRef, view} = useLoadedReportContext();
  const parsedQueryString = queryString.parse(window.location.search);
  const viewer = useViewer();
  const [isTipsSidebarOpen, setIsTipsSidebarOpen] = useState(
    parsedQueryString.firstReport != null
  );

  const views = useSelector(ViewSelectors.getViews);
  const navigateToNewDraft = useNavigateToNewDraft({
    view,
    views,
    entityName,
    projectName,
  });

  // if a user visits a published report in edit mode, we should:
  // redirect to the view mode if the user is not the author
  // redirect to the draft if the user is the author
  // if there are no drafts, make a new one
  if (view.type !== 'runs/draft' && projectName != null && draftId == null) {
    Analytics.track('User visited /edit page for published report', {
      entityName,
      projectName,
      reportID: viewedReportId,
    });
    // if the user is the author of the report, redirect to an editable draft
    if (viewer?.id != null && viewer.id === view.user.id) {
      const userDraft = reportDrafts.data?.view?.children?.edges.find(
        edge =>
          edge?.node?.user?.id != null && edge?.node?.user?.id === viewer?.id
      );
      // if a draft belonging to the user is found, redirect to it
      if (userDraft != null) {
        return (
          <Redirect
            to={Urls.reportEdit(
              {
                entityName,
                projectName,
                reportID: viewedReportId,
                reportName: view.displayName,
              },
              `?draftId=${userDraft.node?.id}`
            )}
          />
        );
      }
      // if no draft is found, create a new draft
      navigateToNewDraft();
    }
    // if the user is not the author of the report, redirect to the report view
    return (
      <Redirect
        to={Urls.reportView({
          entityName,
          projectName,
          reportID: rootReportId,
          reportName: view.displayName,
        })}
      />
    );
  }

  // if users are vewing a draft id with /edit afterwads, redirect to new URL
  if (
    draftId == null &&
    projectName != null &&
    reportParent.data?.viewParent != null
  ) {
    Analytics.track('User visited old <displayname>--<draftId>/edit page', {
      entityName,
      projectName,
      reportID: reportParent.data.viewParent.id,
      draftID: viewedReportId,
    });
    return (
      <Redirect
        to={Urls.reportEdit(
          {
            entityName,
            projectName,
            reportID: reportParent.data.viewParent.id,
            reportName: view.displayName,
          },
          `?draftId=${viewedReportId}`
        )}
      />
    );
  }

  return (
    <>
      <ReportPageEditHeader
        reportWidth={reportWidth}
        isTipsSidebarOpen={isTipsSidebarOpen}
        setIsTipsSidebarOpen={setIsTipsSidebarOpen}
      />
      <S.ReportPageContainer isViewMode={false}>
        <ReportDiscussionContextProvider
          reportViewRef={reportViewRef}
          reportServerID={draftId ?? ''}>
          <ReportTipsSidebar
            open={isTipsSidebarOpen}
            setOpen={setIsTipsSidebarOpen}
          />
          <div
            style={{overflow: 'auto', flex: '1 1 auto', minHeight: 0}}
            data-test="report-page-edit-content">
            <ReportPageEditContentHeader />
            <ReportPageContent />
          </div>
        </ReportDiscussionContextProvider>
      </S.ReportPageContainer>
    </>
  );
};
